import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChefHat, ArrowLeft, Loader2, Check, Building2, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: "",
    business_type: "restaurant",
    owner_name: "",
    owner_email: "",
    owner_phone: "",
    address: "",
    city: "",
    country: "Kenya",
    plan_id: "",
    join_group_code: "",
    create_group: false,
    group_name: "",
    message: ""
  });

  useEffect(() => {
    fetchPlans();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Set plan from URL param
    const planCode = searchParams.get("plan");
    if (planCode && plans.length > 0) {
      const plan = plans.find(p => p.code === planCode);
      if (plan) {
        setFormData(prev => ({ ...prev, plan_id: plan.id }));
      }
    }
  }, [searchParams, plans]);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/plans`);
      setPlans(res.data);
      // Default to first plan if no plan selected
      if (res.data.length > 0 && !formData.plan_id) {
        setFormData(prev => ({ ...prev, plan_id: res.data[0].id }));
      }
    } catch (error) {
      toast.error("Failed to load plans");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.business_name || !formData.owner_name || !formData.owner_email || !formData.owner_phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.plan_id) {
      toast.error("Please select a plan");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.owner_email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Validate phone (Kenya format)
    const phoneRegex = /^(\+254|254|0)?[17]\d{8}$/;
    if (!phoneRegex.test(formData.owner_phone.replace(/\s/g, ''))) {
      toast.error("Please enter a valid Kenyan phone number");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/register`, formData);
      setSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(amount);
  };

  const selectedPlan = plans.find(p => p.id === formData.plan_id);

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for registering with HostPoint. We will review your application and 
              send you an email at <strong>{formData.owner_email}</strong> once your account is approved.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This usually takes 1-2 business days. You&apos;ll receive login credentials via email.
            </p>
            <Button onClick={() => navigate("/")} className="w-full">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-bold text-gray-900">HostPoint</span>
          </Link>
          <Button variant="ghost" onClick={() => navigate("/")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Start Your Free Trial</h1>
          <p className="text-gray-600">
            Fill in your details below. Your account will be reviewed and activated within 1-2 business days.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Business Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-5 w-5" />
                    Business Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="business_name">Business Name *</Label>
                      <Input
                        id="business_name"
                        value={formData.business_name}
                        onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                        placeholder="e.g., Safari Restaurant"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="business_type">Business Type</Label>
                      <Select
                        value={formData.business_type}
                        onValueChange={(value) => setFormData({ ...formData, business_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="hotel">Hotel</SelectItem>
                          <SelectItem value="bar">Bar / Pub</SelectItem>
                          <SelectItem value="cafe">Café</SelectItem>
                          <SelectItem value="fast_food">Fast Food</SelectItem>
                          <SelectItem value="catering">Catering</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="e.g., Nairobi"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="address">Address</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="Street address"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Owner Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="h-5 w-5" />
                    Owner / Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="owner_name">Full Name *</Label>
                      <Input
                        id="owner_name"
                        value={formData.owner_name}
                        onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="owner_email">Email Address *</Label>
                      <Input
                        id="owner_email"
                        type="email"
                        value={formData.owner_email}
                        onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="owner_phone">Phone Number *</Label>
                      <Input
                        id="owner_phone"
                        value={formData.owner_phone}
                        onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                        placeholder="+254 7XX XXX XXX"
                        required
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Group Options (for Enterprise) */}
              {selectedPlan?.is_group_plan && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Group / Chain Options</CardTitle>
                    <CardDescription>
                      Enterprise plans support multiple properties under one group
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="create_group"
                        checked={formData.create_group}
                        onCheckedChange={(checked) => setFormData({ 
                          ...formData, 
                          create_group: checked,
                          join_group_code: checked ? "" : formData.join_group_code
                        })}
                      />
                      <Label htmlFor="create_group">Create a new group/chain</Label>
                    </div>
                    
                    {formData.create_group && (
                      <div>
                        <Label htmlFor="group_name">Group Name</Label>
                        <Input
                          id="group_name"
                          value={formData.group_name}
                          onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                          placeholder="e.g., Safari Hotels Group"
                        />
                      </div>
                    )}

                    {!formData.create_group && (
                      <div>
                        <Label htmlFor="join_group_code">Or join existing group (optional)</Label>
                        <Input
                          id="join_group_code"
                          value={formData.join_group_code}
                          onChange={(e) => setFormData({ ...formData, join_group_code: e.target.value })}
                          placeholder="Enter group code"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave empty if not joining an existing group
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Additional Message */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Additional Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Any additional information you'd like to share (optional)"
                    rows={3}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Plan Selection Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-8">
                <CardHeader>
                  <CardTitle className="text-lg">Select Plan</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : (
                    <RadioGroup
                      value={formData.plan_id}
                      onValueChange={(value) => setFormData({ ...formData, plan_id: value })}
                      className="space-y-3"
                    >
                      {plans.map((plan) => (
                        <div key={plan.id} className="relative">
                          <RadioGroupItem
                            value={plan.id}
                            id={plan.id}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={plan.id}
                            className="flex flex-col p-4 border rounded-lg cursor-pointer hover:bg-gray-50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-semibold">{plan.name}</span>
                              <span className="text-primary font-bold">
                                {formatCurrency(plan.price_monthly)}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">/month</span>
                            <span className="text-xs text-gray-600 mt-2">{plan.description}</span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </CardContent>
                <CardFooter className="flex-col gap-4">
                  <Button type="submit" className="w-full" disabled={submitting || loading}>
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit Registration"
                    )}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    By registering, you agree to our Terms of Service and Privacy Policy.
                  </p>
                </CardFooter>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
